import React, { useMemo, useCallback } from 'react'
import { MainNavbar, Footer } from '@umituz/web-design-system/organisms'
import { useTheme, useLanguage } from '@umituz/web-design-system/hooks'
import { useTranslation } from 'react-i18next'
import { Zap } from 'lucide-react'
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../constants/languages'
import { CONTACT_EMAIL, CONTACT_INFO } from '../constants/contact'
import { GitHubIcon, LinkedInIcon, EmailIcon } from './icons/SocialIcons'

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation()
  const { effectiveTheme, toggleTheme } = useTheme()
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage({
    defaultLanguage: DEFAULT_LANGUAGE,
    supportedLanguages: SUPPORTED_LANGUAGES,
  })

  const navItems = useMemo(
    () => [
      { name: t('nav.home'), path: '/' },
      { name: t('nav.projects'), path: '/projects' },
      { name: t('nav.opensource'), path: '/opensource' },
      { name: t('nav.blog'), path: '/blog' },
      { name: t('nav.about'), path: '/about' },
      { name: t('nav.contact'), path: '/contact' },
    ],
    [t]
  )

  const transformedLanguages = useMemo(
    () =>
      Object.entries(supportedLanguages).reduce(
        (acc, [code, { name, flag }]) => ({ ...acc, [code]: { code, name, flag } }),
        {} as Record<string, { code: string; name: string; flag: string }>
      ),
    [supportedLanguages]
  )

  const navbarTranslations = useMemo(
    () => ({
      language: t('nav.language'),
      switchToMode: (mode: string) => t('nav.switchToMode', { mode }),
      lightMode: t('nav.lightMode'),
      darkMode: t('nav.darkMode'),
      github: t('nav.github'),
    }),
    [t]
  )

  const footerSocial = useMemo(
    () => [
      {
        name: 'GitHub',
        href: CONTACT_INFO.github.url,
        icon: <GitHubIcon />,
      },
      {
        name: 'LinkedIn',
        href: CONTACT_INFO.linkedin.url,
        icon: <LinkedInIcon />,
      },
      {
        name: 'Email',
        href: `mailto:${CONTACT_EMAIL}`,
        icon: <EmailIcon />,
      },
    ],
    []
  )

  const handleLanguageChange = useCallback(
    (code: string) => changeLanguage(code),
    [changeLanguage]
  )

  return (
    <div className="min-h-screen flex flex-col">
      <MainNavbar
        logo={<Zap className="text-primary" size={24} />}
        appName={t('common.appName')}
        navItems={navItems}
        supportedLanguages={transformedLanguages}
        currentLanguage={currentLanguage}
        onLanguageChange={handleLanguageChange}
        theme={effectiveTheme}
        onThemeToggle={toggleTheme}
        githubUrl={CONTACT_INFO.github.url}
        githubLabel={t('nav.github')}
        translations={navbarTranslations}
      />

      <main className="flex-1">{children}</main>

      <Footer
        brand={{
          name: t('footer.brand.title'),
        }}
        social={footerSocial}
        copyright={t('footer.copyright')}
      />
    </div>
  )
}
